// mentorship-en.js — Integrales Mentorship EN (i18n of archive DE content)
// New second-pass keys fall back to DE until EN polish (owner: DE first).
import deContent from './mentorship-de.js'

const ASSET = 'https://media.thelightcall.com/mentorship'

export const meta = {
  title: 'Integral Mentorship — Benjamin Kurtz | The Light Call',
  description: 'Integral Mentorship with Benjamin Kurtz. Commit to a life in WOW. Free Clarity Call.',
}

export const header = {
  nav: [
    { label: 'Mentorship', anchor: '#was-ist' },
    { label: 'For whom?', anchor: '#fuer-wen' },
    { label: 'Results', anchor: '#cases' },
    { label: 'About', anchor: '#ueber' },
    { label: 'Call', anchor: '#booking' },
  ],
  cta: 'Free intro call',
}

export const hero = {
  label: 'INTEGRAL MENTORSHIP',
  headlineLine1: 'Do you want outstanding',
  headlineLine2: 'results?',
  heroCta: 'Commit to a life in WOW!',
  subline: 'Everything okay as it is? No commitment needed. — Integral Mentorship · 1:1 · online',
  ctaPrimary: 'Book Clarity Call',
  ctaSecondary: 'Learn more →',
  videoUrl: '',
  posterUrl: `${ASSET}/kungfu.jpg`,
  vimeoEmbedUrl: '',
}

export const services = {
  items: [
    { label: 'Clarity at the core', sub: 'Who you really are' },
    { label: 'Intensive 1:1 guidance', sub: 'Stay with it — not one-off' },
    { label: 'A life of fullness', sub: 'Results aligned with heart & soul' },
  ],
  location: 'online & worldwide',
}

export const welcome = {
  headline: 'Hi, I am Benjamin.\nGood to have you here.',
  quoteLines: [
    'Here you learn what Integral Mentorship is and how I support you to think, feel, and act anew.',
    'My promise: new perspectives, new approaches, and concrete next steps.',
    'Try it with no obligation. I look forward to hearing from you.',
  ],
  author: 'Benjamin',
  imageUrl: `${ASSET}/benjamin.jpg`,
  signatureUrl: 'https://media.thelightcall.com/images/signature_benjamin_black.png',
}

export const statement = {
  lines: [
    'Is it good the way it is?',
    'Yes? Congratulations.',
    'No? Then it is your responsibility to change something.',
  ],
}

export const quoteSection = {
  id: 'quote',
  surface: 'image',
  backgroundUrl: `${ASSET}/bg-quote-duality.jpg`,
  overlayOpacity: 0.58,
  overlayColor: 'navy',
  quotes: [
    {
      lines: [
        "I hope you live a life you're proud of,",
        "and if you find that you're not,",
        'I hope you have the strength to start all over again.',
      ],
      author: 'F. S. Fitzgerald',
    },
  ],
}

export const whatIs = {
  label: 'WHERE IS THE PROBLEM, REALLY?',
  headline: 'This is usually the part where I talk about your problem 😅',
  body: [
    'I am convinced that an extraordinary life focuses on the solution, not the problem — and you already know best what you long for most.',
    'You already sense that this one life holds countless possibilities. Your life is already rich. You have achieved great things.',
    'The decisive question: "Is it good the way it is?" Yes? Congratulations. No? Then it is your responsibility to change something in your life.',
    'Maybe you cannot yet name exactly what is missing. All you know is that it is time to discover your own path. You have long done everything like everyone else — and you are running out of role models.',
    'You want something "different" — not necessarily more (revenue, money, freedom, variety) — but different. A life that is an exuberant expression of who you are at the core.',
  ],
}

export const forWhom = {
  label: 'FOR WHOM',
  headline: 'Want an answer to what your "different" is?',
  intro: 'Then let us connect and take the first step together. My promise: new perspectives, new approaches, and concrete solutions. Try it with no obligation.',
  topics: [
    { title: 'Entrepreneurs & coaches', body: 'Who sense that revenue is only one piece of the puzzle. Behind financial success often lies the longing for freedom — to do what YOU want.' },
    { title: 'People in transition', body: 'Who did everything "right" like everyone else — and notice that role models run out and a deeper "different" is calling.' },
    { title: 'Seeking clarity', body: 'Who cannot yet name what is missing — only know it is time to find their own path.' },
    { title: 'Commitment-ready', body: 'Who are ready to commit to a life in WOW. Everything okay as it is? No commitment needed.' },
  ],
}

export const about = {
  label: 'ABOUT THE MENTORSHIP',
  headline: 'How about a new way of thinking about your life?',
  body: [
    'Do you believe a 5-millimeter domino can topple a 1-meter domino? Not with "normal" linear cause-and-effect logic.',
    'A domino can topple another 1.5 times its size. With only 12 dominos, a tiny stone can fell a 1m block. All you need is the energy of the first 5mm stone falling. That is exponential thinking.',
    'Your thoughts — and even more, your feelings — manifest immediately. Whether stress with your boss or meeting your dream partner.',
    'Others may call it secrets. I show you how to apply it simply. No fluff. Try and EXPERIENCE.',
    'In integral mentorship I support mentees to think anew in order to feel and act anew — and to find the 5mm domino in their life that can change everything.',
  ],
  credentials: ['Integral Mentorship', 'NLP Master', 'Hypnotherapy', 'Energy work', '1:1 guidance'],
  credentialsLabel: 'APPROACH & METHODS',
  photoPlaceholder: '[ Photo Benjamin Kurtz ]',
}

export const processSection = {
  label: 'A LIFE OF FULLNESS',
  headline: 'How Integral Mentorship catapults you into a life of fullness',
  footnote: 'Three pillars of the work — clarity, guidance, foundation for a fulfilled life.',
  steps: [
    {
      number: '01',
      title: 'Gain clarity about who you are at the core',
      titleParts: [
        { text: 'Gain ', accent: false },
        { text: 'clarity', accent: 'primary' },
        { text: ' about who you are at the core', accent: false },
      ],
      duration: 'Self-knowledge',
      body: 'And what that means for life, relationships, and business. Self-knowledge is the superpower of the future.',
    },
    {
      number: '02',
      title: 'Intensive guidance instead of one-off support',
      titleParts: [
        { text: 'Intensive', accent: 'primary' },
        { text: ' guidance instead of one-off support', accent: false },
      ],
      duration: '1:1 commitment',
      body: 'Regular 1:1 sessions keep you moving. Mentorship is a clear commitment to actively shape your life — including wishes you barely dare to dream yet.',
    },
    {
      number: '03',
      title: 'Lay the foundation for a deeply fulfilled life',
      titleParts: [
        { text: 'Lay the foundation for ', accent: false },
        { text: 'a deeply fulfilled life', accent: 'primary' },
      ],
      duration: 'Outcome',
      body: 'Reach goals with joy and calm, solve challenges efficiently. Step by step results that fulfill you — heart, soul, and mind aligned.',
    },
  ],
  cta: 'Get results now',
}

export const cases = {
  label: 'RESULTS',
  headline: 'These are my clients\' results',
  body: 'In the end it does not matter who you are or where you come from. My clients share one mission: live an extraordinary life.',
  anonymityNote: '',
  clickHint: 'Click a story to read it.',
  items: [
    {
      name: 'Tobias Hummel', gender: 'male', image: `${ASSET}/tobias.jpg`,
      title: 'Emotional Release Coach',
      teaser: 'Physical pain, weak follow-through, doubt about vocation and relationship — then the breakthrough.',
      tag: 'Mentorship · Vocation · Relationship',
      situation: 'Tobias started with 4 coaching sessions. He had strong physical pain, felt weak at implementation, was unhappy at work, and kept doubting his vocation and relationship.',
      session: 'Still mid-mentorship he grew beyond himself. No more doubt about working as a coach; a strong job change ahead; relationship like a honeymoon. He returned to a healthy body sense and to love and intuition.',
      result: '"I am endlessly grateful to Benjamin for this breakthrough. He showed me how powerful my thoughts and feelings are in releasing old pain. Through his support I learned to overcome my greatest fears and empower myself to become a great coach."',
    },
    {
      name: 'Anna Gräfen', gender: 'female', image: `${ASSET}/anna.jpg`,
      title: 'Health Coach',
      teaser: 'After losing her partner: rebuilt the business, 4x revenue, joy of life returned.',
      tag: 'Mentorship · Business · Healing',
      situation: 'Three years before "Leben in WOW!" mentorship, Anna lost her partner in a tragic accident. She had pushed a lot away, hardened through bodybuilding and work, often felt alone and overwhelmed, and missed depth with clients.',
      session: 'Within 3 months she rebuilt her business from hard "Forged of Steel" to "Anna Gräfen Health Coach" with a new site, a 30-day course, and holistic mentorships. She 4x\'d revenue in 4 months while creating more time for herself.',
      result: '"Thank you for today\'s role-play in our mentorship! It helped me become much more confident in my work — I applied it in the very next session. I am making huge leaps through our collaboration. Simply thank you 🙏"',
    },
    {
      name: 'Sabine Hinkel', gender: 'female', image: `${ASSET}/sabine.jpg`,
      title: 'Founder Dreamsketcher · Youth coach',
      teaser: 'Trusted Benjamin 100% — but not herself. A year later: own company, healed intolerance, extended mentorship.',
      tag: 'Mentorship · Founding · Health',
      situation: '"When I started working with Benjamin a year ago, I trusted him 100% — but not myself. I lacked professional and social fulfillment and wanted more from life." She also suffered for 7 years under severe fructose intolerance.',
      session: 'Today she works from the heart as a youth and children\'s coach and founded Dreamsketcher with live workshops and online courses. She has extended mentorship three times.',
      result: '"One year of mentoring with Benjamin fundamentally changed my life. His sensitive, supportive way helped me open up and meet challenges. He helped me overcome years of fructose intolerance and live more freely. Benjamin is an extraordinary mentor I fully trust."',
    },
    {
      name: 'Bianca Gruber',
      gender: 'female',
      image: '',
      title: 'NLP Master Practitioner',
      teaser:
        'Motherhood, protection, and closeness — finding a place in her heart for herself and her child again.',
      tag: 'Mentorship · Family · Closeness',
      situation:
        '"I thought I could not love him the way a mother should. And I did not want him to love me the way I had always longed for deep inside — simply because I did not want to hurt or be hurt."',
      session:
        'In mentorship Bianca found a way to allow closeness — to herself, to her child, and to what she truly wants.',
      result:
        '"Benjamin is the best coach, trainer, and mentor I can imagine. His way of working and his deep understanding of people make him an extraordinary companion. Since our work together, I have given my little star a place in my heart — and my son has a special place there too. That shows in both of our behavior. I would not have believed so much could change so quickly, inside and out. I hold it in my own hands."',
    },
  ],
  sectionLabels: { situation: 'Starting point', session: 'In mentorship', result: 'Client voice' },
}

export const simpleCta = {
  label: 'LET US CONNECT',
  headline: '30 minutes, you & me.',
  body: 'My promise: new perspectives, new approaches, and concrete next steps. You can only gain.',
  cta: 'Book Clarity Call',
}

export const ctaImage = {
  label: 'READY?',
  headline: 'Get results now',
  subline: 'Free. No obligation. 30 minutes. Book your Clarity Call.',
  cta: 'Secure a time',
  imageUrl: `${ASSET}/benjamin.jpg`,
  imageAlt: 'Benjamin Kurtz',
}

export const booking = {
  label: 'CLARITY CALL',
  headline: 'Book your Clarity Call now.',
  subline: 'Free and non-binding. 30 minutes. I look with you at what moves you — and whether Integral Mentorship is the next sensible step.',
  formPlaceholder: '',
  directBookingText: 'Want to pick a slot directly?',
  directBookingCta: 'Book Clarity Call',
  directBookingUrl: 'https://tidycal.com/bkacademy/mentorship',
  calendarUrl: 'https://tidycal.com/bkacademy/mentorship?embedded=1',
  formTopics: ['Business & vocation', 'Relationships', 'Clarity / purpose', 'Manifestation', 'Personal growth', 'Other'],
}

export const faq = {
  bigLabel: 'Q & A',
  headline: 'Common questions about Integral Mentorship',
  items: [
    { question: 'What is Integral Mentorship?', answer: 'Intensive 1:1 guidance to think, feel, and act anew — finding the 5mm domino in your life that can change everything. Regular sessions, not one-off tips.' },
    { question: 'Do I need to commit before the Clarity Call?', answer: 'No. The call is non-binding. Everything okay as it is? No commitment needed. Want outstanding results? I talk with you about what commitment means for you.' },
    { question: 'Who is it for?', answer: 'People who sense that "more of the same" is not enough — entrepreneurs, coaches, people in transition — ready to take responsibility for change.' },
    { question: 'Online or in person?', answer: 'Usually online, worldwide. In person by arrangement.' },
  ],
}

export const footer = {
  brand: 'Benjamin Kurtz Academy LLC',
  disclaimerLinkKeyword: 'Clarity Call',
  tagline: 'Integral Mentorship\nThe Light Call',
  contactLabel: 'Contact me:',
  legalLabel: 'Legal',
  followLabel: 'Follow',
  reachOutLabel: 'REACH OUT',
  disclaimerTitle: 'Legal notice',
  disclaimer: 'Mentorship and coaching are not a substitute for medical or psychiatric treatment. If in doubt, you are welcome to book a Clarity Call — I can advise you competently.',
  introCallLabel: 'Clarity Call',
  copyright: { prefix: 'All rights reserved. Home:', homeLabel: 'thelightcall.com', homeUrl: 'https://thelightcall.com' },
  contact: [
    { label: 'WhatsApp', url: 'https://wa.me/4915121231244', icon: 'whatsapp' },
    { label: 'Email', url: 'mailto:hello@benjaminkurtz.de', icon: 'mail' },
    { label: 'Instagram', url: 'https://instagram.com/benjaminkurtz_de', icon: 'instagram' },
  ],
  legalLinks: [
    { label: 'Terms', url: 'https://www.benjaminkurtz.de/terms/' },
    { label: 'Privacy', url: 'https://www.benjaminkurtz.de/privacy/' },
    { label: 'Imprint', url: 'https://www.benjaminkurtz.de/imprint/' },
  ],
  social: [
    { label: 'Spotify', url: 'https://open.spotify.com/' },
    { label: 'Apple', url: 'https://podcasts.apple.com/' },
    { label: 'YouTube', url: 'https://youtube.com/' },
  ],
}

export const researchers = { authorBigLabel: '', authorHeadline: '', authorLabel: '', accordion: { readMore: 'Read more', readLess: 'Read less' }, authors: [] }
export const references = { label: '', headline: '', items: [] }
export const testimonials = { label: '', headline: '', items: [] }
export const podcasts = []
export const podcastVideo = {}
export const ui = { showMore: 'More', showLess: 'Less', menuOpen: 'Close menu', menuClosed: 'Open menu' }
export const transcriptPage = {}
export const notFound = { title: 'Page not found', body: 'This page does not exist here.', cta: 'Home' }

export const menu = {
  overviewLabel: 'Overview',
  header: {
    logo: { src: 'https://media.thelightcall.com/logos/BKA%20logo%20500%20px%20black.png', alt: 'Benjamin Kurtz Academy' },
    cta: { label: 'Clarity Call', anchor: '#booking' },
    variant: 'sidecar',
    mainNav: [
      { label: 'Mentorship', anchor: null, hasSubmenu: true },
      { label: 'Results', anchor: null, hasSubmenu: true },
      { label: 'Call', anchor: '#booking', hasSubmenu: false },
    ],
  },
  items: [
    { id: 'mentorship', label: 'Mentorship', order: 10, children: [
      { id: 'm-hero', label: 'Start', anchor: '#hero', order: 11 },
      { id: 'm-what', label: 'The problem', anchor: '#was-ist', order: 12 },
      { id: 'm-for', label: 'For whom?', anchor: '#fuer-wen', order: 13 },
      { id: 'm-how', label: 'How it works', anchor: '#ueber', order: 14 },
      { id: 'm-process', label: 'Life of fullness', anchor: '#prozess', order: 15 },
    ]},
    { id: 'results', label: 'Results', order: 20, children: [
      { id: 'm-cases', label: 'Clients', anchor: '#cases', order: 21 },
      { id: 'm-faq', label: 'FAQ', anchor: '#faq', order: 22 },
    ]},
    { id: 'sites', label: 'The Light Call', order: 30, children: [
      { id: 's-reg', label: 'Regression', href: '/regression/en', order: 31 },
      { id: 's-men', label: 'Mentorship', href: '/mentorship/en', order: 32 },
      { id: 's-rei', label: 'Reiki', href: '/reiki/en', order: 33 },
    ]},
  ],
}

export default {
  ...deContent,
  meta,
  header,
  hero,
  services,
  welcome,
  statement,
  quoteSection,
  whatIs,
  forWhom,
  about,
  processSection,
  cases,
  simpleCta,
  ctaImage,
  booking,
  faq,
  footer,
  ui,
  researchers,
  references,
  testimonials,
  podcasts,
  podcastVideo,
  transcriptPage,
  notFound,
  menu,
}
